import React from 'react';
import HeaderOne from '../header/HeaderOne';
import FooterOne from '../footer/FooterOne';
import ContactForm from '../components/ContactForm';
import Head from '../components/Head';
import ChatBubble from '../components/ChatBubble';
import {
  LuSparkles, LuMessageCircle, LuMail, LuArrowRight, LuClock
} from 'react-icons/lu';
import './Contact.css';

// Click handler for the "Chat now" button — finds the floating ChatBubble
// launcher and clicks it. Avoids lifting state out of ChatBubble.
const openChatBubble = () => {
  const launcher = document.querySelector('.cb-bubble');
  if (launcher) launcher.click();
};

export default function Contact() {
  return (
    <>
      <Head
        title="Contact Support — SMS Gateway Help"
        description="Get help with TextLink. AI assistant, Discord community, and direct contact form for questions about SMS gateway setup, GoHighLevel integration, billing, and carrier coverage."
        canonical="/Contact"
      />
      <HeaderOne />

      <main className="ct">
        <div className="ct-shell">

          {/* ---- Hero ---- */}
          <header className="ct-hero">
            <span className="ct-eyebrow">Support</span>
            <h1 className="ct-h1">Get help — fastest path first</h1>
            <p className="ct-lead">
              Most questions get answered in seconds by our AI assistant or in our Discord community. The contact form is here too, but you'll usually get a faster answer through the options above it.
            </p>
          </header>

          {/* ---- Primary support options ---- */}
          <section className="ct-options">

            {/* AI Assistant — the recommended path */}
            <article className="ct-option ct-option--primary">
              <span className="ct-tag">Recommended · instant</span>
              <div className="ct-option-icon">
                <LuSparkles size={20} strokeWidth={1.75} />
              </div>
              <h2 className="ct-option-title">Ask the AI assistant</h2>
              <p className="ct-option-text">
                Trained on every page of our docs, every common setup question, every billing FAQ, every GHL gotcha. It answers in seconds and links you straight to the right page.
              </p>
              <ul className="ct-option-list">
                <li>Setup, pairing, and device questions</li>
                <li>GoHighLevel installation and connection</li>
                <li>API, webhook, and integration help</li>
                <li>Billing and refunds</li>
              </ul>
              <button type="button" className="ct-btn ct-btn--primary" onClick={openChatBubble}>
                Chat now <LuArrowRight size={15} strokeWidth={2} />
              </button>
            </article>

            {/* Discord */}
            <article className="ct-option">
              <span className="ct-tag ct-tag--green">Community · usually under an hour</span>
              <div className="ct-option-icon">
                <LuMessageCircle size={20} strokeWidth={1.75} />
              </div>
              <h2 className="ct-option-title">Join the Discord</h2>
              <p className="ct-option-text">
                The team is in there daily, and so are 100+ other agencies running TextLink. Great for nuanced questions, real-time troubleshooting, and seeing what others have built.
              </p>
              <ul className="ct-option-list">
                <li>Direct line to the team</li>
                <li>Other users help each other</li>
                <li>Roadmap previews, feature flags, beta invites</li>
              </ul>
              <a
                href="https://discord.gg/quhcCHPaZQ"
                target="_blank"
                rel="noopener noreferrer"
                className="ct-btn ct-btn--ghost"
              >
                Open Discord <LuArrowRight size={15} strokeWidth={2} />
              </a>
            </article>

          </section>

          {/* ---- Secondary: email + office ---- */}
          <section className="ct-secondary">
            <div className="ct-secondary-card">
              <div className="ct-secondary-icon">
                <LuMail size={16} strokeWidth={1.75} />
              </div>
              <div>
                <h3>Email us directly</h3>
                <p>
                  <a href="mailto:aaronb@textlinksms.com">aaronb@textlinksms.com</a> for support and product questions.<br />
                  <a href="mailto:sales@textlinksms.com">sales@textlinksms.com</a> for billing, plans, and partnerships.
                </p>
              </div>
            </div>

            <div className="ct-secondary-card">
              <div className="ct-secondary-icon">
                <LuClock size={16} strokeWidth={1.75} />
              </div>
              <div>
                <h3>Response time</h3>
                <p>
                  AI assistant: instant.<br />
                  Discord: usually within an hour.<br />
                  Email: within 24 hours, weekdays.
                </p>
              </div>
            </div>
          </section>

          {/* ---- Form (fallback) ---- */}
          <section className="ct-form-section">
            <header className="ct-form-head">
              <h2 className="ct-form-title">Or send us a message</h2>
              <p className="ct-form-sub">
                If you'd rather write it out — go ahead. We'll reply by email. (Reminder: the AI assistant in the corner answers most questions in seconds, no waiting.)
              </p>
            </header>
            <div className="ct-form-wrapper">
              <ContactForm />
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
      <ChatBubble />
    </>
  );
}
